<template>
  <v-app>
    <v-app-bar app color="white" class="virtual-app-bar">
      <v-app-bar-nav-icon @click.stop="drawer = !drawer" />
      <img src="/images/front/virtual-logo.png" class="virtual-top-logo" />
      <v-spacer />
      <v-btn
        icon
        v-for="btn in toolbarButtons"
        :key="btn.path"
        @click="toolbarAction(btn.path)"
      >
        <i :class="'fal fa-' + btn.icon + ' fa-fw'"></i>
      </v-btn>
      <notifications v-if="showNotifications" />

      <settings-icon/>

      <div v-if="user && showScore" class="appbar-points">
        <v-progress-circular
          v-if="loadingTotalPoints"
          indeterminate
          color="white"
          size="22"
          width="2"
        />
        <template v-else>
          {{ totalPoints }} {{ $t('layout.points_short') }}
        </template>
      </div>
    </v-app-bar>

    <v-main>
      <div v-if="user" class="fill-height">
        <v-navigation-drawer
          class="primary virtual-drawer"
          app
          v-model="drawer"
          dark
        >
          <v-list dense nav>
            <template v-if="user">
              <v-list-item>
                <v-list-item-action>
                  <v-icon left>mdi-account-circle-outline</v-icon>
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title
                    >{{ user.general_field5 }}</v-list-item-title
                  >
                </v-list-item-content>
              </v-list-item>
              <v-divider />
            </template>

            <template v-if="showRoomsSelect">
              <v-list-item @click="navigateTo('/', false)">
                <v-list-item-action>
                  <i class="fal fa-fw fa-exchange" />
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title v-if="switchMenuTitle">{{
                    switchMenuTitle
                  }}</v-list-item-title>
                  <v-list-item-title v-else>{{
                    $t('layout.live_broadcasting')
                  }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-divider />
            </template>

            <template v-if="hasShowrooms">
              <v-list-item @click="navigateTo({ name: 'showrooms' })">
                <v-list-item-action>
                  <i class="fal fa-fw fa-store" />
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title>{{
                    $t('layout.virtual_showrooms')
                  }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-divider />
            </template>

            <v-list-item
              v-for="menuItem in menu"
              :key="menuItem.id"
              :disabled="!menuItem.enabled"
              @click="navigateTo(menuItem)"
            >
              <v-list-item-action>
                <i
                  v-if="menuItem.icon"
                  class="fal fa-fw"
                  :class="`fa-${menuItem.icon}`"
                />
              </v-list-item-action>
              <v-list-item-content>
                <v-list-item-title>
                  {{ menuItem.name }}
                  <sup v-if="menuItem.module == 'external_link'"
                    ><i
                      class="fal fa-external-link"
                      aria-hidden="true"
                      style="font-size: 100%"
                    ></i
                  ></sup>
                </v-list-item-title>
              </v-list-item-content>

              <v-list-item-action
                v-if="unreadMessagesCount != 0 && menuItem.module == 'messages'"
              >
                <span class="badge">{{ unreadMessagesCount }}</span>
              </v-list-item-action>

              <v-list-item-action
                v-if="
                  unreadNetworkingMessagesIds.length != 0 &&
                    menuItem.module == 'networking'
                "
              >
                <span class="badge">{{
                  unreadNetworkingMessagesIds.length
                }}</span>
              </v-list-item-action>
            </v-list-item>

            <template v-if="user">
              <v-divider />
              <v-list-item @click="logout" class="mt-1">
                <v-list-item-action>
                  <i class="fal fa-sign-out-alt fa-fw"></i>
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title>{{ $t('layout.logout') }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>

              <!-- <cookies /> -->

            </template>
          </v-list>
        </v-navigation-drawer>
        <in-page-messages />
        <div id="viewer">
          <room-select
            v-if="isSelectRoomShowed"
            layout="virtualMobile"
          />
          <video-stream v-else-if="!isShowRoomModule" layout="virtualMobile" />
          <router-view :key="$route.fullPath" name="viewer" />
        </div>

        <smileys-swiper />

        <partners-swiper v-if="showPartnersBar" class="py-2" />

        <div
          v-if="isInteractiveViewVisible"
          class="interactive-view interactive-view-mobile"
          :style="{ height: contentHeight }"
        >
          <div class="interactive-container">
            <div class="interactive-content">
              <transition name="router-view">
                <router-view :key="$route.fullPath" />
              </transition>
            </div>
          </div>
        </div>
      </div>
      <login v-else />
    </v-main>
    <v-footer app inset class="pr-2" v-if="footer.enabled">
      <v-spacer />
      <span v-html="footer.content" />
    </v-footer>
  </v-app>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import Notifications from '../components/toolbar/notifications'
import PartnersSwiper from '../components/views/PartnersSwiper'
import SmileysSwiper from '../components/views/SmileysSwiper'
import Login from '../components/login'
import RoomSelect from '../components/rooms-select'
import VideoStream from '../components/video-stream'
import scrollMixin from '../mixins/scroll'
import navigateMixin from '../mixins/navigate'
import viewportMixin from '../mixins/viewport'
import InPageMessages from '../components/in-page-messages'
import SettingsIcon from '../components/settings/icon'

export default {
  mixins: [scrollMixin, navigateMixin, viewportMixin],

  components: {
    Notifications,
    PartnersSwiper,
    SmileysSwiper,
    Login,
    RoomSelect,
    VideoStream,
    InPageMessages,
    SettingsIcon
  },

  data() {
    return {
      drawer: false,
      contentHeight: '100%',
    }
  },

  computed: {
    ...mapGetters('common', {
      showNotifications: 'showNotifications',
      menu: 'rootMenu',
    }),

    ...mapGetters({
      toolbarButtons: 'buttons',
      unreadMessagesCount: 'unreadMessagesCount',
      unreadNetworkingMessagesIds: 'unreadNetworkingMessagesIds',
      hasShowrooms: 'showrooms/hasShowrooms',
    }),

    ...mapState({
      user: state => state.auth.user,
      totalPoints: state => state.auth.totalPoints,
      loadingTotalPoints: state => state.auth.loadingTotalPoints,
      rooms: state => state.common.rooms,
      showScore: state => state.common.virtual.showScore,
      partnersBar: state => state.common.virtual.partnersBar,
      switchMenuTitle: state => state.common.virtual.multipleRooms.menuItemName,
      footer: state => state.common.footer,
      interactiveViewHidden: state => state.common.interactiveViewHidden,
      smileysEnabled: state => state.common.virtual.smileysEnabled,
      showRoomsSelect: state => state.common.virtual.showRoomsSelect,
    }),

    isShowRoomModule() {
      return ['showroom', 'showrooms'].includes(this.$route.name)
    },

    isInteractiveViewVisible() {
      return this.$route.name != 'showrooms' && !this.interactiveViewHidden
    },

    isSelectRoomShowed() {
      return (
        this.showRoomsSelect &&
        !this.$route.params.roomId &&
        !this.isShowRoomModule
      )
    },

    showPartnersBar() {
      return this.partnersBar.show && !this.isShowRoomModule
    },

    viewerHeight() {
      const appBarHeight = this.$vuetify.breakpoint.md ? 64 : 56

      let height = this.viewport.height - appBarHeight
      if (this.showPartnersBar) {
        height -= 90
      }

      if (this.footer.enabled) {
        height -= 33
      }

      return `${height}px`
    },
  },

  mounted() {
    if (this.user && this.showScore) {
      this.$store.dispatch('auth/getTotalPoints')
    }

    this.calcContentHeight()
    window.addEventListener('resize', this.calcContentHeight)

    if (this.$route.path != '/') {
      this.scrollUnderVideo()
    }
  },

  methods: {
    calcContentHeight() {
      const appBarHeight = this.$vuetify.breakpoint.md ? 64 : 56
      const wh = this.viewport.height
      let height = wh - appBarHeight
      if (this.footer.enabled) {
        height -= 32
      }
      this.contentHeight = `${height}px`
    },
  },
}
</script>
